import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { generateChapterLink, generateWorkLink } from '@/utils/utils';
import { getObraImageUrl, PLACEHOLDER_OBRA_COVER } from '@/utils/image-utils';
import type { ReadingTabEntry } from '@/utils/reading-tabs';
import { removeReadingTab } from '@/utils/reading-tabs';

type Props = {
    tabs: ReadingTabEntry[];
    activeCapId: number | null;
    onNavigate?: () => void;
    variant?: 'sheet' | 'compact';
};

export default function ReadingTabsListContent({ tabs, activeCapId, onNavigate, variant = 'sheet' }: Props) {
    const rowPad = variant === 'sheet' ? 'px-4 py-3' : 'px-2 py-1.5';
    const titleCls = variant === 'sheet' ? 'text-base font-semibold text-white' : 'text-xs font-semibold text-white/90';

    if (tabs.length === 0) {
        return (
            <p className="px-4 py-10 text-center text-sm text-white/45">
                Abre um capítulo para aparecer aqui. Guardamos até 6 obras (último capítulo de cada).
            </p>
        );
    }

    return (
        <ul className={variant === 'sheet' ? 'divide-y divide-white/[0.06]' : 'flex flex-col gap-1'}>
            {tabs.map((tab) => {
                const isActive = activeCapId != null && tab.capId === activeCapId;
                const capHref = generateChapterLink(tab.capId);
                const obraHref = generateWorkLink(tab.obraId);
                const thumbUrl = getObraImageUrl(tab.obraId, tab.obraImagem ?? null);
                const thumb = thumbUrl ?? PLACEHOLDER_OBRA_COVER;

                return (
                    <li key={tab.obraId}>
                        <div
                            className={`flex items-center gap-2 sm:gap-3 ${rowPad} ${
                                isActive ? 'bg-orange-500/10' : 'hover:bg-white/[0.04]'
                            } transition-colors`}
                        >
                            <Link
                                to={capHref}
                                onClick={onNavigate}
                                className="flex min-w-0 flex-1 items-center gap-3"
                            >
                                <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-white/10 bg-white/[0.04]">
                                    <img
                                        src={thumb}
                                        alt=""
                                        className="h-full w-full object-cover"
                                        onError={(e) => {
                                            e.currentTarget.src = PLACEHOLDER_OBRA_COVER;
                                        }}
                                    />
                                </div>
                                <div className="min-w-0 flex-1 text-left">
                                    <p className={`truncate ${titleCls}`}>{tab.obraNome}</p>
                                    <p className="truncate text-sm text-orange-400/95">{tab.capLabel}</p>
                                </div>
                            </Link>
                            <div className="flex shrink-0 flex-col items-end gap-1">
                                <Link
                                    to={obraHref}
                                    onClick={onNavigate}
                                    className="text-[11px] text-white/40 hover:text-white/70 whitespace-nowrap"
                                >
                                    Obra
                                </Link>
                                <button
                                    type="button"
                                    className="rounded-lg p-2 text-white/35 hover:bg-white/[0.08] hover:text-white/80"
                                    aria-label={`Remover ${tab.obraNome} das abas rápidas`}
                                    onClick={() => removeReadingTab(tab.obraId)}
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}
